import { Box, Button, Flex, Portal, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import {
  getBannerBottomPx,
  getPageChromePx,
  useVisualViewportInset,
} from './pwa-install-banner-layout';
import {
  markTimedPrompt,
  shouldOfferTimedPromptFromStorage,
  WEB_PUSH_PROMPT_KEY,
} from './timed-prompt';
import { WebPushPermissionGuide } from './WebPushPermissionGuide';

type WebPushPermissionBannerProps = {
  isVisible: boolean;
  isBlocked?: boolean;
  onEnable: () => Promise<NotificationPermission | null>;
};

export function WebPushPermissionBanner({
  isVisible,
  isBlocked = false,
  onEnable,
}: WebPushPermissionBannerProps) {
  const router = useRouter();
  const visualInsetPx = useVisualViewportInset();
  const pageChromePx = getPageChromePx(router.pathname);
  const bannerBottomPx = getBannerBottomPx(router.pathname, visualInsetPx);
  const sitsOnBottomNav = pageChromePx > 0;
  const [isDismissed, setIsDismissed] = useState(
    () => !shouldOfferTimedPromptFromStorage(WEB_PUSH_PROMPT_KEY)
  );
  const [isRequesting, setIsRequesting] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  const dismiss = useCallback(() => {
    markTimedPrompt(WEB_PUSH_PROMPT_KEY);
    setIsDismissed(true);
  }, []);

  const enable = useCallback(async () => {
    if (isRequesting) return;
    if (isBlocked) {
      markTimedPrompt(WEB_PUSH_PROMPT_KEY);
      setIsDismissed(true);
      setIsGuideOpen(true);
      return;
    }

    setIsRequesting(true);
    let permission: NotificationPermission | null = null;
    try {
      permission = await onEnable();
    } catch {
      permission = null;
    } finally {
      setIsRequesting(false);
    }
    markTimedPrompt(WEB_PUSH_PROMPT_KEY);
    setIsDismissed(true);
    if (permission !== 'granted') setIsGuideOpen(true);
  }, [isBlocked, isRequesting, onEnable]);

  const showBanner = isVisible && !isDismissed;

  return (
    <>
      {showBanner ? (
        <Portal>
          <Flex
            position="fixed"
            bottom={{
              base: sitsOnBottomNav
                ? `calc(${bannerBottomPx}px + env(safe-area-inset-bottom, 0px))`
                : `${bannerBottomPx}px`,
              md: `${visualInsetPx}px`,
            }}
            left={0}
            right={0}
            bg="white"
            borderTopWidth="1px"
            borderColor="gray.200"
            px={{ base: 3, md: 4 }}
            pt={3}
            pb={
              sitsOnBottomNav
                ? 3
                : {
                    base: 'calc(env(safe-area-inset-bottom) + 0.75rem)',
                    md: 3,
                  }
            }
            direction={{ base: 'column', md: 'row' }}
            align={{ base: 'stretch', md: 'center' }}
            justify="space-between"
            gap={3}
            zIndex="overlay"
            boxShadow="0 -2px 10px rgba(0,0,0,0.1)"
          >
            <Flex align="center" gap={3} flex={1} minW={0}>
              <Text fontSize="xl" lineHeight={1}>
                <span role="img" aria-label="Notifikasi">
                  &#128276;
                </span>
              </Text>
              <Box minW={0}>
                <Text fontWeight="semibold" fontSize="sm">
                  {isBlocked ? 'Notifikasi Diblokir' : 'Aktifkan Notifikasi'}
                </Text>
                <Text fontSize="xs" color="gray.500" overflowWrap="anywhere">
                  {isBlocked
                    ? 'Ubah izin situs ini di pengaturan browser'
                    : 'Dapatkan update pesanan dan info penting'}
                </Text>
              </Box>
            </Flex>
            <Flex
              gap={2}
              ml={{ base: 0, md: 3 }}
              w={{ base: 'full', md: 'auto' }}
              minW={0}
            >
              <Button
                size="sm"
                variant="ghost"
                minH="44px"
                minW={0}
                flex={{ base: 1, md: 'initial' }}
                onClick={dismiss}
              >
                Nanti Saja
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                minH="44px"
                minW={0}
                flex={{ base: 1, md: 'initial' }}
                onClick={enable}
                isLoading={isRequesting}
              >
                {isBlocked ? 'Cara Mengaktifkan' : 'Aktifkan'}
              </Button>
            </Flex>
          </Flex>
        </Portal>
      ) : null}
      <WebPushPermissionGuide
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
      />
    </>
  );
}
